import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Upload, FileText, ClipboardPaste, Loader2, PlayCircle, CheckCircle2,
  AlertTriangle, XCircle, Copy, Download, RefreshCw, Building2, Link2,
  ChevronLeft, Users, Printer, MessageCircle, Table2, Plus, Trash2,
  FileDown, Columns3,
} from 'lucide-react';
import api from '../services/api';

// Bulk condo onboarding admin UI (2026-10-08). Backend contract:
//   POST /admin/bulk-onboarding/preview   { condoId, csv }
//   POST /admin/bulk-onboarding/execute   { condoId, rows }
//   GET  /admin/bulk-onboarding/runs/:id
//   POST /admin/bulk-onboarding/runs/:id/resend-failed
// Classification values: new_stub | existing_link | invalid | duplicate.
// Optional CSV columns (Jaime addendum): building_or_section, floor,
// internal_address — they only apply to newly created units.
//
// Bulk-onboarding UX overhaul (Jaime request 2026-10-08): template download +
// editable grid with column-mapping dropdowns. The BACKEND CONTRACT STAYS
// UNCHANGED — all mapping happens client-side:
//  - The editable grid builds the CSV in the browser using the per-column
//    mapping dropdowns ("Ignorar" columns are excluded) before POST /preview.
//  - `street` (Calle) and `house_number` (Casa) are NOT backend columns. They
//    are client-side-only helpers that get merged into `internal_address` as
//    structured text ("Calle X, Casa Y") when building the CSV. Do not invent
//    new backend columns for them.

const BATCH_CAP = 500;

// Canonical field set + Spanish aliases used for header auto-mapping when
// pasting from a spreadsheet and in the HELP text / template.
type CanonicalField = {
  key: string;
  required: boolean;
  sample: string;
  aliases: string[];
};

// Region-aware Spanish display labels (Jaime round 2, 2026-10-08). Canonical
// keys (unit_number, owner_email, ...) must NEVER surface in the UI, help
// text, column dropdowns or the downloaded template -- only fieldLabel()
// output is shown. Canonical keys remain only in client<->backend CSV
// round-trips.
type RegionCode = 'default' | 'VE' | 'MX' | 'CO' | 'AR';

const FIELD_LABELS: Record<string, Record<RegionCode, string>> = {
  unit_number:         { default: 'Número/Nombre de Unidad', VE: 'Número/Nombre de Unidad', MX: 'Número/Nombre de Unidad', CO: 'Número/Nombre de Unidad', AR: 'Número/Nombre de Unidad' },
  name:                { default: 'Nombre', VE: 'Nombre', MX: 'Nombre', CO: 'Nombre', AR: 'Nombre' },
  owner_email:         { default: 'Email del Propietario', VE: 'Email del Propietario', MX: 'Correo electrónico del Propietario', CO: 'Correo del Propietario', AR: 'Correo del Propietario' },
  owner_phone:         { default: 'WhatsApp del Propietario', VE: 'WhatsApp del Propietario', MX: 'WhatsApp del Propietario', CO: 'Celular del Propietario', AR: 'Celular del Propietario' },
  building_or_section: { default: 'Edificio/Torre o Sección', VE: 'Edificio/Torre o Sección', MX: 'Edificio/Torre o Sección', CO: 'Edificio/Torre o Sección', AR: 'Edificio/Torre o Sección' },
  floor:               { default: 'Piso/Nivel', VE: 'Piso/Nivel', MX: 'Piso/Nivel', CO: 'Piso/Nivel', AR: 'Piso/Nivel' },
  street:              { default: 'Calle (clusters)', VE: 'Calle (clusters)', MX: 'Calle (clusters)', CO: 'Calle (clusters)', AR: 'Calle (clusters)' },
  house_number:        { default: 'Casa (número o nombre)', VE: 'Casa (número o nombre)', MX: 'Casa (número o nombre)', CO: 'Casa (número o nombre)', AR: 'Casa (número o nombre)' },
  internal_address:    { default: 'Dirección interna / Notas', VE: 'Dirección interna / Notas', MX: 'Dirección interna / Notas', CO: 'Dirección interna / Notas', AR: 'Dirección interna / Notas' },
};

function fieldLabel(key: string, region?: string): string {
  const r: RegionCode = region === 'VE' || region === 'MX' || region === 'CO' || region === 'AR' ? region : 'default';
  return FIELD_LABELS[key]?.[r] || FIELD_LABELS[key]?.default || key;
}

const CANONICAL_FIELDS: CanonicalField[] = [
  { key: 'unit_number', required: true, sample: '101', aliases: ['unidad', 'unit', 'numero', 'número', 'apto', 'apartamento', 'departamento', 'número/nombre de unidad', 'número o nombre de unidad', 'número de unidad', 'número/nombre de la unidad'] },
  { key: 'name', required: true, sample: 'Ana Torres', aliases: ['nombre', 'propietario', 'owner', 'nombre propietario', 'nombre del propietario'] },
  { key: 'owner_email', required: false, sample: 'ana@example.com', aliases: ['correo', 'email', 'mail', 'correo electronico', 'correo electrónico', 'email del propietario', 'correo electrónico del propietario', 'correo del propietario'] },
  { key: 'owner_phone', required: false, sample: '+56912345678', aliases: ['telefono', 'teléfono', 'whatsapp', 'phone', 'celular', 'movil', 'móvil', 'whatsapp del propietario', 'celular del propietario', 'móvil del propietario', 'teléfono del propietario'] },
  { key: 'building_or_section', required: false, sample: 'Torre A', aliases: ['edificio', 'torre', 'seccion', 'sección', 'bloque', 'edificio/torre o sección', 'edificio o sección'] },
  { key: 'floor', required: false, sample: '3', aliases: ['piso', 'nivel', 'piso/nivel', 'piso o nivel'] },
  // street / house_number: client-side only — merged into internal_address.
  { key: 'street', required: false, sample: 'Las Acacias', aliases: ['calle', 'street', 'calle (clusters)', 'calle clusters'] },
  { key: 'house_number', required: false, sample: '12', aliases: ['casa', 'numero casa', 'número casa', 'house', 'casa (número o nombre)', 'número o nombre de casa'] },
  { key: 'internal_address', required: false, sample: 'Portón 2', aliases: ['direccion interna', 'dirección interna', 'direccion', 'dirección', 'notas', 'nota', 'observaciones', 'dirección interna / notas', 'dirección interna o notas'] },
];

const FIELD_BY_KEY = new Map(CANONICAL_FIELDS.map((f) => [f.key, f]));
const IGNORE = '';

// Map a pasted/CSV header (Spanish display label, regional variant or legacy
// alias) to a canonical field key, or '' when unrecognized. Comparison is
// accent- and punctuation-insensitive so template headers like
// "Número/Nombre de Unidad" match regardless of region.
function normHeader(s: string): string {
  return String(s || '').trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}
function squashHeader(s: string): string {
  return normHeader(s).replace(/[^a-z0-9]/g, '');
}
function aliasToField(header: string): string {
  const s = normHeader(header);
  if (!s) return IGNORE;
  const q = squashHeader(header);
  for (const f of CANONICAL_FIELDS) {
    if (s === f.key || f.aliases.includes(s)) return f.key;
    if (q && (q === squashHeader(f.key) || f.aliases.some((a) => squashHeader(a) === q))) return f.key;
  }
  return IGNORE;
}

// Translate the header row of an uploaded CSV from Spanish display labels (or
// any legacy alias) to canonical keys before POSTing to the backend, so the
// backend contract stays canonical. No-op when no header is recognized.
function translateCsvHeaders(text: string): string {
  const lines = text.replace(/\r\n?/g, '\n').split('\n');
  if (!lines.length) return text;
  const head = lines[0].split(',');
  const mapped = head.map((h) => aliasToField(h.replace(/^"|"$/g, '').trim()));
  if (!mapped.some((f) => f !== IGNORE)) return text;
  lines[0] = head.map((h, i2) => (mapped[i2] === IGNORE ? h.trim() : mapped[i2])).join(',');
  return lines.join('\n');
}

type ClassRow = {
  row?: number;
  unit_number: string;
  email: string;
  phone?: string;
  name: string;
  building_or_section?: string;
  floor?: string;
  internal_address?: string;
  classification: 'new_stub' | 'existing_link' | 'invalid' | 'duplicate';
  channel?: string;
  errors?: string[];
  emailMasked?: string | null;
  existingName?: string | null;
};

type RunResult = ClassRow & {
  userId?: string;
  status?: string;
  error?: string;
  token?: string;
  emailStatus?: string;
  owner_conflict?: boolean;
  expiresAt?: string;
  // WhatsApp claim card (phone-only rows; backend in flight 2026-10-08):
  // prefilled wa.me URL incl. claim token. Field name not finalized — accept
  // the likely spellings until the backend lane lands.
  waUrl?: string;
  wa_url?: string;
  waLink?: string;
  wa?: string;
  whatsapp_url?: string;
};

type RunTotals = Record<string, number>;
type RunData = { runId: string; condoName?: string; totals: RunTotals; results: RunResult[]; status?: string };

// Editable grid model: each column carries a mapping (canonical key or
// '' = Ignorar); rows are plain string cells aligned with gridCols.
type GridCol = { id: number; field: string };

const CLASS_STYLE: Record<string, { label: string; cell: string; badge: string }> = {
  new_stub: { label: 'Crear propietario', cell: 'bg-green-50 dark:bg-green-900/20', badge: 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100' },
  existing_link: { label: 'Vincular existente', cell: 'bg-blue-50 dark:bg-blue-900/20', badge: 'bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-100' },
  invalid: { label: 'Inválida', cell: 'bg-red-50 dark:bg-red-900/20', badge: 'bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100' },
  duplicate: { label: 'Duplicada', cell: 'bg-amber-50 dark:bg-amber-900/20', badge: 'bg-amber-100 text-amber-800 dark:bg-amber-800 dark:text-amber-100' },
};

const STATUS_LABEL: Record<string, string> = {
  new_stub: 'Creada', existing_link: 'Vinculada', already_linked: 'Ya vinculada',
  invalid: 'Inválida', duplicate: 'Duplicada', failed: 'Fallida',
};

function claimUrl(token: string) {
  return `${window.location.origin}/accept-invite?token=${encodeURIComponent(token)}`;
}

function waOf(r: RunResult): string | null {
  return r.waUrl || r.wa_url || r.waLink || r.wa || r.whatsapp_url || null;
}

// Minimal RFC-4180-ish escaping for the client-built CSV.
function csvCell(v: string): string {
  return /[",\n\r]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
}

// Split pasted TSV/CSV text into rows of cells. TSV (spreadsheet copy) wins;
// single-column-with-commas falls back to comma splitting.
function parsePastedCells(text: string): string[][] {
  const lines = text.replace(/\r\n?/g, '\n').split('\n').filter((l) => l.trim() !== '');
  const rows = lines.map((l) => l.split('\t').map((c) => c.trim()));
  const singleCol = rows.length > 0 && rows.every((r) => r.length === 1 && r[0].includes(','));
  if (singleCol) return lines.map((l) => l.split(',').map((c) => c.trim()));
  return rows;
}

export default function BulkOnboardingPage() {
  const [condos, setCondos] = useState<Array<{ id: string; name: string; country_code?: string }>>([]);
  const [condoId, setCondoId] = useState('');
  const [condosLoading, setCondosLoading] = useState(true);
  const [mode, setMode] = useState<'grid' | 'file'>('grid');
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [csvText, setCsvText] = useState('');
  const [fileName, setFileName] = useState('');
  const [preview, setPreview] = useState<ClassRow[] | null>(null);
  const [previewError, setPreviewError] = useState('');
  const [previewing, setPreviewing] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [run, setRun] = useState<RunData | null>(null);
  const [runLoading, setRunLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [copied, setCopied] = useState(false);
  const [topError, setTopError] = useState('');
  const condoRegion = condos.find((c) => c.id === condoId)?.country_code || '';

  // --- Editable grid state ---
  const nextColId = useRef(4);
  const [gridCols, setGridCols] = useState<GridCol[]>([
    { id: 1, field: 'unit_number' },
    { id: 2, field: 'name' },
    { id: 3, field: 'owner_email' },
  ]);
  const [gridRows, setGridRows] = useState<string[][]>([[], [], []].map(() => ['', '', '']));
  const [templateFields, setTemplateFields] = useState<Set<string>>(
    () => new Set(['unit_number', 'name', 'owner_email'])
  );

  const gridRowCount = useMemo(() => gridRows.filter((r) => r.some((c) => c.trim() !== '')).length, [gridRows]);

  const ensureRowWidth = (rows: string[][], n: number) =>
    rows.map((r) => (r.length === n ? r : [...r.slice(0, n), ...Array(Math.max(0, n - r.length)).fill('')]));

  const addGridRow = () => {
    if (gridRows.length >= BATCH_CAP) return;
    setGridRows((rs) => [...ensureRowWidth(rs, gridCols.length), Array(gridCols.length).fill('')]);
  };

  const removeGridRow = (idx: number) => {
    setGridRows((rs) => rs.filter((_, i) => i !== idx));
  };

  const setCell = (ri: number, ci: number, v: string) => {
    setGridRows((rs) => rs.map((row, i) => (i === ri ? row.map((c, j) => (j === ci ? v : c)) : row)));
  };

  const setColField = (colId: number, field: string) => {
    setGridCols((cs) => cs.map((c) => (c.id === colId ? { ...c, field } : c)));
  };

  const addGridCol = () => {
    // Pick the first canonical field not already mapped, else leave Ignorar.
    const used = new Set(gridCols.map((c) => c.field));
    const free = CANONICAL_FIELDS.find((f) => !used.has(f.key));
    setGridCols((cs) => [...cs, { id: nextColId.current++, field: free ? free.key : IGNORE }]);
    setGridRows((rs) => ensureRowWidth(rs, gridCols.length + 1));
  };

  const removeGridCol = (colId: number) => {
    if (gridCols.length <= 1) return;
    const idx = gridCols.findIndex((c) => c.id === colId);
    setGridCols((cs) => cs.filter((c) => c.id !== colId));
    setGridRows((rs) => rs.map((r) => r.filter((_, i) => i !== idx)));
  };

  // Paste from spreadsheet: TSV/CSV -> cells. If the first row looks like a
  // header row (any cell maps to a canonical field), apply the mapping to the
  // columns and drop that row.
  const applyPastedText = (text: string) => {
    const cells = parsePastedCells(text);
    if (!cells.length) return;
    const headerFields = cells[0].map(aliasToField);
    const looksLikeHeader = headerFields.some((f) => f !== IGNORE);
    if (looksLikeHeader) {
      const mapped = headerFields.map((f) => ({ id: nextColId.current++, field: f }));
      setGridCols(mapped);
      setGridRows(() => ensureRowWidth(cells.slice(1, BATCH_CAP), mapped.length));
    } else {
      const n = Math.max(gridCols.length, ...cells.map((r) => r.length));
      setGridCols((cs) => {
        const out = [...cs];
        while (out.length < n) {
          const used = new Set(out.map((c) => c.field));
          const free = CANONICAL_FIELDS.find((f) => !used.has(f.key));
          out.push({ id: nextColId.current++, field: free ? free.key : IGNORE });
        }
        return out;
      });
      setGridRows((rs) => ensureRowWidth([...rs, ...cells.slice(0, BATCH_CAP)], n));
    }
    setPreview(null);
  };

  const pasteFromClipboard = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (!text.trim()) { setPreviewError('El portapapeles está vacío.'); return; }
      applyPastedText(text);
      setPreviewError('');
    } catch {
      setPreviewError('No se pudo leer el portapapeles. Usa Ctrl+V dentro de la tabla.');
    }
  };

  const onGridPaste = (e: React.ClipboardEvent) => {
    const text = e.clipboardData.getData('text/plain');
    if (!text || (!text.includes('\n') && !text.includes('\t'))) return; // normal cell editing
    e.preventDefault();
    applyPastedText(text);
  };

  // Build the CSV client-side from the grid using the column mappings.
  // street/house_number merge into internal_address as "Calle X, Casa Y"
  // (client-side only — the backend has no such columns).
  const buildGridCsv = (): { csv: string; error: string } => {
    const used = gridCols.filter((c) => c.field !== IGNORE);
    const has = (k: string) => used.some((c) => c.field === k);
    if (!has('unit_number') || !has('name')) {
      return { csv: '', error: `La tabla necesita columnas mapeadas a \u00ab${fieldLabel('unit_number', condoRegion)}\u00bb y \u00ab${fieldLabel('name', condoRegion)}\u00bb.` };
    }
    const headers: string[] = [];
    for (const f of CANONICAL_FIELDS) {
      if (f.key === 'street' || f.key === 'house_number') continue;
      if (has(f.key)) headers.push(f.key);
    }
    const mergesAddress = has('street') || has('house_number');
    if (mergesAddress && !has('internal_address')) headers.push('internal_address');
    const lines = [headers.join(',')];
    for (const row of gridRows) {
      if (!row.some((c) => c.trim() !== '')) continue;
      const rec: Record<string, string> = {};
      for (let ci = 0; ci < gridCols.length; ci++) {
        const field = gridCols[ci]?.field;
        if (field && field !== IGNORE) rec[field] = (row[ci] || '').trim();
      }
      if (mergesAddress) {
        const parts: string[] = [];
        if (rec.internal_address) parts.push(rec.internal_address);
        if (rec.street) parts.push(`Calle ${rec.street}`);
        if (rec.house_number) parts.push(`Casa ${rec.house_number}`);
        rec.internal_address = parts.join(', ');
      }
      lines.push(headers.map((h) => csvCell(rec[h] || '')).join(','));
    }
    return { csv: lines.join('\n'), error: '' };
  };

  // --- Template download (feature 1) ---
  const toggleTemplateField = (key: string) => {
    setTemplateFields((s) => {
      const out = new Set(s);
      if (out.has(key)) out.delete(key); else out.add(key);
      return out;
    });
  };

  const downloadTemplate = () => {
    const keys = CANONICAL_FIELDS.filter((f) => f.required || templateFields.has(f.key)).map((f) => f.key);
    // Spanish, region-aware headers — the admin opens this file in Excel.
    const lines = [keys.map((k) => csvCell(fieldLabel(k, condoRegion))).join(',')];
    // Two clearly-fake sample rows (example.com emails / placeholder phones)
    // so the formato is self-explanatory; preview flags anything invalid.
    for (const n of [1, 2]) {
      lines.push(keys.map((k) => {
        let v = FIELD_BY_KEY.get(k)?.sample || '';
        if (k === 'unit_number') v = String(100 + n);
        if (k === 'name') v = n === 1 ? 'Ana Torres (ejemplo)' : 'Luis Ríos (ejemplo)';
        if (k === 'owner_email') v = n === 1 ? 'ana@example.com' : 'luis@example.com';
        if (k === 'owner_phone') v = n === 1 ? '+56912345678' : '+56987654321';
        if (k === 'street') v = 'Las Acacias';
        if (k === 'house_number') v = String(10 + n);
        return csvCell(v);
      }).join(','));
    }
    const blob = new Blob(['\uFEFF' + lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'plantilla-alta-masiva.csv';
    a.click();
    URL.revokeObjectURL(a.href);
  };

  useEffect(() => {
    api.get('/condos').then((res) => {
      const data = res.data?.data ?? res.data;
      const list = Array.isArray(data) ? data : data?.items ?? data?.condos ?? [];
      setCondos(list.map((c: any) => ({ id: c.id, name: c.name, country_code: c.country_code })));
    }).catch(() => setTopError('No se pudo cargar la lista de condominios.'))
      .finally(() => setCondosLoading(false));
  }, []);

  const counts = useMemo(() => {
    const acc: Record<string, number> = {};
    for (const r of preview || []) acc[r.classification] = (acc[r.classification] || 0) + 1;
    return acc;
  }, [preview]);

  const totalRows = preview?.length ?? 0;
  const invalidCount = (counts.invalid || 0) + (counts.duplicate || 0);
  const previewOk = !!preview && totalRows > 0 && totalRows <= BATCH_CAP;
  const runFailures = useMemo(() => (run?.results || []).filter(
    (r) => ['failed', 'skipped_flag_off'].includes(r.emailStatus || '') || r.status === 'failed'
  ), [run]);
  const tokensAvailable = useMemo(() => (run?.results || []).some((r) => !!r.token), [run]);
  const waCards = useMemo(() => (run?.results || []).map((r) => ({ r, wa: waOf(r) })).filter((x) => !!x.wa) as Array<{ r: RunResult; wa: string }>, [run]);

  const onFile = (f: File | null) => {
    if (!f) return;
    setFileName(f.name);
    const reader = new FileReader();
    reader.onload = () => setCsvText(String(reader.result || ''));
    reader.onerror = () => setPreviewError('No se pudo leer el archivo.');
    reader.readAsText(f);
  };

  const doPreview = async () => {
    setTopError('');
    if (!condoId) { setPreviewError('Selecciona un condominio.'); return; }
    let payload = translateCsvHeaders(csvText);
    if (mode === 'grid') {
      const { csv, error } = buildGridCsv();
      if (error) { setPreviewError(error); return; }
      if (gridRowCount === 0) { setPreviewError('Agrega al menos una fila con datos.'); return; }
      payload = csv;
    }
    if (!payload.trim()) { setPreviewError('Pega el CSV, carga un archivo o completa la tabla.'); return; }
    setPreviewing(true); setPreviewError(''); setPreview(null); setRun(null);
    try {
      const res = await api.post('/admin/bulk-onboarding/preview', { condoId, csv: payload });
      setPreview(res.data?.data?.rows ?? []);
    } catch (e: any) {
      setPreviewError(e?.response?.data?.error || 'No se pudo procesar el CSV.');
    } finally {
      setPreviewing(false);
    }
  };

  const doExecute = async () => {
    if (!previewOk || !preview) return;
    setTopError(''); setExecuting(true);
    try {
      const res = await api.post('/admin/bulk-onboarding/execute', { condoId, rows: preview });
      const data = res.data?.data;
      setRun({ runId: data.runId, condoName: data.condoName, totals: data.totals || {}, results: data.results || [], status: data.status });
    } catch (e: any) {
      setTopError(e?.response?.data?.error || 'No se pudo ejecutar la carga.');
    } finally {
      setExecuting(false);
    }
  };

  const refreshRun = async (id: string) => {
    setRunLoading(true);
    try {
      const res = await api.get(`/admin/bulk-onboarding/runs/${id}`);
      const d = res.data?.data;
      setRun({ runId: d.id, condoName: d.condo_name, totals: d.totals || {}, results: d.results || [], status: d.status });
    } catch { /* keep current view */ } finally { setRunLoading(false); }
  };

  const doResend = async () => {
    if (!run) return;
    setTopError(''); setResending(true);
    try {
      await api.post(`/admin/bulk-onboarding/runs/${run.runId}/resend-failed`, {});
      await refreshRun(run.runId);
    } catch (e: any) {
      setTopError(e?.response?.data?.error || 'No se pudo reenviar.');
    } finally { setResending(false); }
  };

  const copyLinks = async () => {
    if (!run) return;
    const lines = (run.results || []).filter((r) => r.token)
      .map((r) => `${r.unit_number}\t${r.email || r.phone || ''}\t${r.name}\t${claimUrl(r.token || '')}`);
    try {
      await navigator.clipboard.writeText(lines.join('\n'));
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch { /* clipboard denied */ }
  };

  const downloadLinks = () => {
    if (!run) return;
    const lines = ['Unidad,Contacto,Nombre,Enlace'];
    for (const r of run.results || []) {
      if (!r.token) continue;
      lines.push(`"${r.unit_number}","${r.email || r.phone || ''}","${r.name}","${claimUrl(r.token || '')}"`);
    }
    const blob = new Blob(['\uFEFF' + lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `enlaces-alta-${run.runId}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const copyWaAll = async () => {
    if (!waCards.length) return;
    const lines = waCards.map(({ r, wa }) => `${r.unit_number}\t${r.phone || ''}\t${r.name}\t${wa}`);
    try {
      await navigator.clipboard.writeText(lines.join('\n'));
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch { /* clipboard denied */ }
  };

  const printWaAll = () => {
    if (!waCards.length) return;
    const w = window.open('', '_blank', 'width=800,height=900');
    if (!w) return;
    const esc = (t: string) => String(t || '').replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c] as string));
    const cards = waCards.map(({ r, wa }) => `<div style="border:1px solid #ddd;border-radius:12px;padding:16px;margin:12px 0;page-break-inside:avoid;font-family:sans-serif"><div style="font-size:20px;font-weight:700">Unidad ${esc(r.unit_number)} — ${esc(r.name)}</div><div style="color:#555;margin:6px 0">${esc(r.phone || '')}</div><div style="word-break:break-all;color:#128C7E">${esc(wa)}</div></div>`).join('');
    w.document.write('<!doctype html><html><head><title>Tarjetas WhatsApp</title></head><body style="margin:24px"><h2 style="font-family:sans-serif">Tarjetas de alta por WhatsApp</h2>' + cards + '</body></html>');
    w.document.close();
    w.focus();
    w.print();
  };

  const backToInput = () => { setRun(null); setPreview(null); setCsvText(''); setFileName(''); };

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Users className="w-6 h-6 text-purple-600" /> Alta masiva de propietarios
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Carga unidades y propietarios desde una tabla o un CSV. Máximo {BATCH_CAP} filas por corrida.
          </p>
        </div>
        {run && (
          <button onClick={backToInput} className="inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200">
            <ChevronLeft className="w-4 h-4" /> Nueva carga
          </button>
        )}
      </div>

      {topError && <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-sm">{topError}</div>}

      {run ? (
        <div className="space-y-4">
          <div className="bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700 p-4 space-y-2">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
              <span className="font-semibold text-gray-900 dark:text-white">Corrida {run.runId} {run.status ? `(${run.status})` : ''}</span>
              {runLoading && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
            </div>
            <div className="flex flex-wrap gap-2 text-xs">
              {[
                ['Total', run.totals.total], ['Creadas', run.totals.new_stub], ['Vinculadas', run.totals.existing_link],
                ['Ya vinculadas', run.totals.already_linked], ['Inválidas', run.totals.invalid], ['Duplicadas', run.totals.duplicate],
                ['Unidades creadas', run.totals.units_created], ['Tokens', run.totals.tokens_issued],
                ['Emails enviados', run.totals.emails_sent], ['Emails fallidos', run.totals.emails_failed],
                ['Emails omitidos', run.totals.emails_skipped],
              ].map(([label, v]) => (
                <span key={String(label)} className="px-2 py-1 rounded-full bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-200">
                  {label}: <b>{Number(v ?? 0)}</b>
                </span>
              ))}
            </div>
            {tokensAvailable && (
              <div className="flex flex-wrap items-center gap-2 pt-2 border-t dark:border-slate-700">
                <span className="text-xs text-amber-700 dark:text-amber-300 flex items-center gap-1">
                  <AlertTriangle className="w-3.5 h-3.5" /> El envío automático está desactivado: entrega estos enlaces manualmente.
                </span>
                <button onClick={copyLinks} className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-lg bg-purple-50 text-purple-700 dark:bg-purple-900/40 dark:text-purple-200 hover:bg-purple-100">
                  {copied ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />} Copiar enlaces
                </button>
                <button onClick={downloadLinks} className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-lg bg-purple-50 text-purple-700 dark:bg-purple-900/40 dark:text-purple-200 hover:bg-purple-100">
                  <Download className="w-3.5 h-3.5" /> Descargar CSV
                </button>
              </div>
            )}
            {runFailures.length > 0 && (
              <div className="pt-2 border-t dark:border-slate-700">
                <button onClick={doResend} disabled={resending} className="inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg bg-red-50 text-red-700 dark:bg-red-900/40 dark:text-red-200 hover:bg-red-100 disabled:opacity-50">
                  {resending ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                  Reenviar a fallidos ({runFailures.length})
                </button>
              </div>
            )}
          </div>

          {waCards.length > 0 && (
            <div className="bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700 p-4 space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="font-semibold text-gray-900 dark:text-white text-sm">Tarjetas WhatsApp ({waCards.length})</span>
                <div className="flex gap-2">
                  <button onClick={copyWaAll} className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-lg bg-purple-50 text-purple-700 dark:bg-purple-900/40 dark:text-purple-200 hover:bg-purple-100">
                    {copied ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />} Copiar todo
                  </button>
                  <button onClick={printWaAll} className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-lg bg-purple-50 text-purple-700 dark:bg-purple-900/40 dark:text-purple-200 hover:bg-purple-100">
                    <Printer className="w-3.5 h-3.5" /> Imprimir todo
                  </button>
                </div>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Filas solo con teléfono: entrega la tarjeta WhatsApp manualmente al propietario (sin SMS).</p>
              <div className="grid gap-2 md:grid-cols-2">
                {waCards.map(({ r, wa }, i) => (
                  <div key={i} className="rounded-lg border dark:border-slate-700 p-3 flex items-center justify-between gap-2 bg-green-50 dark:bg-green-900/20">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-gray-900 dark:text-white truncate">Unidad {r.unit_number} — {r.name || '—'}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">{r.phone || '—'}</div>
                    </div>
                    <a href={wa} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-lg bg-green-600 text-white hover:bg-green-700 whitespace-nowrap">
                      <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
                    </a>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700 overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs uppercase text-gray-500 border-b dark:border-slate-700">
                <th className="p-3">Unidad</th><th className="p-3">Contacto</th><th className="p-3">Nombre</th>
                <th className="p-3">Estado</th><th className="p-3">Email</th><th className="p-3">Detalle</th>
              </tr></thead>
              <tbody className="divide-y dark:divide-slate-700">
                {(run.results || []).map((r, i) => (
                  <tr key={i}>
                    <td className="p-3 font-semibold text-gray-900 dark:text-white">{r.unit_number || '—'}</td>
                    <td className="p-3 text-gray-600 dark:text-gray-300">{r.emailMasked || r.email || r.phone || '—'}</td>
                    <td className="p-3 text-gray-600 dark:text-gray-300">{r.name || '—'}</td>
                    <td className="p-3"><span className={`px-2 py-0.5 rounded-full text-xs ${r.status === 'invalid' || r.status === 'failed' || r.status === 'duplicate' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>{STATUS_LABEL[r.status || ''] || r.status || '—'}</span>{r.owner_conflict && <span className="ml-1 text-[10px] text-amber-700">(conflicto de propietario)</span>}</td>
                    <td className="p-3 text-xs text-gray-500">{r.emailStatus || '—'}</td>
                    <td className="p-3 text-xs text-gray-500">{r.error || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700 p-4 space-y-3">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-200">
              <Building2 className="w-4 h-4" /> Condominio
            </label>
            <select
              value={condoId}
              onChange={(e) => { setCondoId(e.target.value); setPreview(null); }}
              disabled={condosLoading}
              className="w-full md:w-96 px-3 py-2 rounded-lg border dark:border-slate-600 bg-white dark:bg-slate-900 text-gray-900 dark:text-white"
            >
              <option value="">{condosLoading ? 'Cargando…' : 'Selecciona un condominio'}</option>
              {condos.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>

          <div className="bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700 p-4 space-y-3">
            <div className="flex flex-wrap gap-2">
              <button onClick={() => setMode('grid')} className={`inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg ${mode === 'grid' ? 'bg-purple-600 text-white' : 'bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-200'}`}>
                <Table2 className="w-4 h-4" /> Tabla editable
              </button>
              <button onClick={() => setMode('file')} className={`inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg ${mode === 'file' ? 'bg-purple-600 text-white' : 'bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-200'}`}>
                <Upload className="w-4 h-4" /> Desde archivo
              </button>
            </div>

            {/* Template download (feature 1): column picker + CSV download. */}
            <div className="rounded-lg border dark:border-slate-700 p-3 space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-200 flex items-center gap-1">
                  <FileDown className="w-4 h-4" /> Plantilla CSV
                </span>
                <button onClick={downloadTemplate} className="inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg bg-purple-50 text-purple-700 dark:bg-purple-900/40 dark:text-purple-200 hover:bg-purple-100">
                  <Download className="w-4 h-4" /> Descargar plantilla CSV
                </button>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {CANONICAL_FIELDS.map((f) => {
                  const on = f.required || templateFields.has(f.key);
                  return (
                    <button
                      key={f.key}
                      type="button"
                      disabled={f.required}
                      onClick={() => toggleTemplateField(f.key)}
                      title={f.required ? 'Siempre incluida' : on ? 'Incluida — clic para quitar' : 'Excluida — clic para incluir'}
                      className={`px-2 py-0.5 rounded-full text-xs border ${on ? 'bg-purple-100 text-purple-800 border-purple-300 dark:bg-purple-900/40 dark:text-purple-200 dark:border-purple-700' : 'bg-gray-100 text-gray-500 border-gray-200 dark:bg-slate-700 dark:text-gray-400 dark:border-slate-600'} ${f.required ? 'opacity-80 cursor-default' : 'hover:bg-purple-50 dark:hover:bg-purple-900/20'}`}
                    >
                      {on ? '✓ ' : '+ '}{fieldLabel(f.key, condoRegion)}
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                unit_number y name siempre van incluidas. street (Calle) y house_number (Casa) se combinan en internal_address como “Calle X, Casa Y” al previsualizar (solo cliente; el backend no tiene esas columnas).
              </p>
            </div>

            {mode === 'file' ? (
              <>
                <div
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={(e) => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files && e.dataTransfer.files[0]; if (f) onFile(f); }}
                  className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${dragOver ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20' : 'border-gray-300 dark:border-slate-600 hover:border-purple-400'}`}
                >
                  <input ref={fileInputRef} type="file" accept=".csv,text/csv,text/plain" className="hidden" onChange={(e) => { onFile(e.target.files?.[0] ?? null); e.currentTarget.value = ''; }} />
                  <p className="text-sm text-gray-700 dark:text-gray-300">Arrastra tu archivo CSV aquí, o <span className="text-purple-600 dark:text-purple-400 underline">búscalo en tu equipo</span></p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">máximo 500 filas por corrida</p>
                  {fileName && <p className="text-xs text-gray-500 mt-1 flex items-center gap-1 justify-center"><FileText className="w-3 h-3" />{fileName}</p>}
                </div>
                <textarea
                  value={csvText}
                  onChange={(e) => { setCsvText(e.target.value); setPreview(null); }}
                  placeholder={`${fieldLabel('unit_number', condoRegion)},${fieldLabel('owner_email', condoRegion)},${fieldLabel('name', condoRegion)}\n101,ana@correo.com,Ana Torres\n102,luis@correo.com,Luis Ríos`}
                  rows={8}
                  className="w-full px-3 py-2 rounded-lg border dark:border-slate-600 bg-white dark:bg-slate-900 text-gray-900 dark:text-white font-mono text-sm"
                />
              </>
            ) : (
              <div className="space-y-2" onPaste={onGridPaste}>
                <div className="flex flex-wrap items-center gap-2">
                  <button onClick={addGridCol} className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-lg bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200">
                    <Columns3 className="w-3.5 h-3.5" /> Agregar columna
                  </button>
                  <button onClick={pasteFromClipboard} className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-lg bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200">
                    <ClipboardPaste className="w-3.5 h-3.5" /> Pegar desde planilla
                  </button>
                  <button onClick={addGridRow} disabled={gridRows.length >= BATCH_CAP} className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-lg bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 disabled:opacity-50">
                    <Plus className="w-3.5 h-3.5" /> Agregar fila
                  </button>
                  <span className={`text-xs ${gridRowCount > BATCH_CAP ? 'text-red-600 font-semibold' : 'text-gray-500 dark:text-gray-400'}`}>
                    {gridRowCount} fila{gridRowCount === 1 ? '' : 's'} de datos
                    {gridRowCount >= BATCH_CAP && <span className="ml-1 text-amber-600">· límite {BATCH_CAP} alcanzado</span>}
                  </span>
                </div>
                <div className="overflow-x-auto border dark:border-slate-700 rounded-lg">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs bg-gray-50 dark:bg-slate-900 border-b dark:border-slate-700">
                        <th className="p-1 w-8 text-gray-400">#</th>
                        {gridCols.map((c) => (
                          <th key={c.id} className="p-1 min-w-[10rem]">
                            <div className="flex items-center gap-1">
                              <select
                                value={c.field}
                                onChange={(e) => { setColField(c.id, e.target.value); setPreview(null); }}
                                className="w-full px-1.5 py-1 text-xs rounded border dark:border-slate-600 bg-white dark:bg-slate-900 text-gray-900 dark:text-white"
                              >
                                <option value={IGNORE}>Ignorar</option>
                                {CANONICAL_FIELDS.map((f) => {
                                  const taken = gridCols.some((o) => o.id !== c.id && o.field === f.key);
                                  if (taken) return null;
                                  return <option key={f.key} value={f.key}>{fieldLabel(f.key, condoRegion)}</option>;
                                })}
                              </select>
                              <button onClick={() => removeGridCol(c.id)} title="Quitar columna" className="text-gray-400 hover:text-red-500">
                                <XCircle className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </th>
                        ))}
                        <th className="p-1 w-8"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y dark:divide-slate-700">
                      {gridRows.map((row, ri) => (
                        <tr key={ri} className="hover:bg-gray-50 dark:hover:bg-slate-900/40">
                          <td className="p-1 text-xs text-gray-400 text-center">{ri + 1}</td>
                          {row.map((cell, ci) => (
                            <td key={ci} className="p-0 border-l dark:border-slate-700 first:border-l-0">
                              <input
                                value={cell}
                                onChange={(e) => { setCell(ri, ci, e.target.value); setPreview(null); }}
                                className="w-full px-2 py-1.5 bg-transparent focus:bg-white dark:focus:bg-slate-900 outline-none text-gray-900 dark:text-white"
                              />
                            </td>
                          ))}
                          <td className="p-1 text-center">
                            <button onClick={() => removeGridRow(ri)} title="Quitar fila" className="text-gray-400 hover:text-red-500">
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Copia un rango desde Excel/Google Sheets y pégalo aquí (Ctrl+V o el botón “Pegar desde planilla”). Si la primera fila son encabezados, se usa para mapear columnas automáticamente.
                </p>
              </div>
            )}

            <p className="text-xs text-gray-500 dark:text-gray-400">
              <b>{fieldLabel('unit_number', condoRegion)}</b> y <b>{fieldLabel('name', condoRegion)}</b> son obligatorias. Además, al menos un contacto: {fieldLabel('owner_email', condoRegion)} o {fieldLabel('owner_phone', condoRegion)}. Opcionales: {fieldLabel('building_or_section', condoRegion)}, {fieldLabel('floor', condoRegion)}, {fieldLabel('street', condoRegion)} y {fieldLabel('house_number', condoRegion)} (se combinan en {fieldLabel('internal_address', condoRegion)} como “Calle X, Casa Y” al previsualizar). Los opcionales solo se aplican a unidades nuevas. Aceptamos encabezados en español o alias habituales (unidad, correo, teléfono…). </p>
            <button onClick={doPreview} disabled={previewing} className="inline-flex items-center gap-1 px-4 py-2 text-sm rounded-lg bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50">
              {previewing ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlayCircle className="w-4 h-4" />} Previsualizar
            </button>
            {previewError && <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-sm flex items-center gap-1"><XCircle className="w-4 h-4" />{previewError}</div>}
          </div>

          {preview && (
            <div className="bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700 p-4 space-y-3">
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <span className="font-semibold text-gray-700 dark:text-gray-200">{totalRows} filas</span>
                <span className={`px-2 py-0.5 rounded-full ${CLASS_STYLE.new_stub.badge}`}>Crear: {counts.new_stub || 0}</span>
                <span className={`px-2 py-0.5 rounded-full ${CLASS_STYLE.existing_link.badge}`}>Vincular: {counts.existing_link || 0}</span>
                <span className={`px-2 py-0.5 rounded-full ${CLASS_STYLE.invalid.badge}`}>Inválidas: {counts.invalid || 0}</span>
                <span className={`px-2 py-0.5 rounded-full ${CLASS_STYLE.duplicate.badge}`}>Duplicadas: {counts.duplicate || 0}</span>
                {totalRows > BATCH_CAP && (
                  <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-800 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" /> Se supera el límite de {BATCH_CAP} filas
                  </span>
                )}
              </div>
              <div className="overflow-x-auto max-h-[28rem] overflow-y-auto border dark:border-slate-700 rounded-lg">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-white dark:bg-slate-800"><tr className="text-left text-xs uppercase text-gray-500 border-b dark:border-slate-700">
                    <th className="p-2">#</th><th className="p-2">Unidad</th><th className="p-2">Contacto</th><th className="p-2">Nombre</th>
                    <th className="p-2">{fieldLabel('building_or_section', condoRegion)}</th><th className="p-2">{fieldLabel('floor', condoRegion)}</th><th className="p-2">{fieldLabel('internal_address', condoRegion)}</th>
                    <th className="p-2">Clasificación</th><th className="p-2">Detalle</th>
                  </tr></thead>
                  <tbody className="divide-y dark:divide-slate-700">
                    {preview.map((r, i) => {
                      const st = CLASS_STYLE[r.classification] || CLASS_STYLE.invalid;
                      return (
                        <tr key={i} className={st.cell}>
                          <td className="p-2 text-xs text-gray-500">{r.row ?? i + 1}</td>
                          <td className="p-2 font-semibold text-gray-900 dark:text-white">{r.unit_number || '—'}</td>
                          <td className="p-2">{r.emailMasked || r.email || r.phone || '—'}</td>
                          <td className="p-2">{r.name || '—'}</td>
                          <td className="p-2 text-xs">{r.building_or_section || '—'}</td>
                          <td className="p-2 text-xs">{r.floor || '—'}</td>
                          <td className="p-2 text-xs">{r.internal_address || '—'}</td>
                          <td className="p-2"><span className={`px-2 py-0.5 rounded-full text-xs whitespace-nowrap ${st.badge}`}>{st.label}</span>{r.channel && r.classification !== 'invalid' && <span className="ml-1 text-[10px] text-gray-500">{r.channel}</span>}</td>
                          <td className="p-2 text-xs text-red-700 dark:text-red-300">{(r.errors || []).join('; ')}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={doExecute}
                  disabled={!previewOk || executing || invalidCount === totalRows}
                  className="inline-flex items-center gap-1 px-4 py-2 text-sm rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
                >
                  {executing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
                  Confirmar y ejecutar ({(counts.new_stub || 0) + (counts.existing_link || 0)} aplicables)
                </button>
                {invalidCount > 0 && <span className="text-xs text-amber-700 dark:text-amber-300">Las filas inválidas/duplicadas se omitirán.</span>}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
